import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_CONTACT_BY_ID_QUERY, GET_CONTACTS_QUERY } from '../../queries/ContactQueries';
import { DELETE_CONTACT_BY_ID_MUTATION } from '../../mutations/ContactMutations';
import type { Contact } from '../../models/Contact';

export default function ContactShow() {
  const { contactId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getContactById] = useLazyQuery(
    GET_CONTACT_BY_ID_QUERY);

  const [deleteContactById] = useMutation(
    DELETE_CONTACT_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_CONTACTS_QUERY }],
  });

  const [contact, setContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(contactId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getContactByIdError } = await getContactById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getContactByIdError) {
          setError(getContactByIdError);
        }

        if (data?.contact) {
          setContact(data.contact);
        } else {
          setError({ message: 'No contact found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [contactId, getContactById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/contacts/${contactId}/edit`);
  }, [contactId, navigate]);

  const handleDelete = useCallback(async () => {
    if (!contact) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${contact?.name}?`,
      {
        title: `Delete contact?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteContactById({ variables: { id: contact.id } });

        if (result?.deleted) {
          navigate('/dashboard/contacts');

          notifications.show('Contact deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete contact. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete contact. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [contact, dialogs, deleteContactById, navigate, notifications]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/contacts');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return contact ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Name</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {contact.name}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Email</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {contact.email}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Phone</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {contact.phone}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Box>
    ) : null;
  }, [isLoading, error, contact, handleBack, handleEdit, handleDelete]);

  const pageTitle = contact?.name || 'N/A';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Contacts', path: '/dashboard/contacts' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
